import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO, generateWebsiteJsonLd } from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { validateEmail, validatePassword, checkRateLimit, logSecurityEvent } from '@/lib/validation';

const Auth = () => {
  // SEO Configuration
  useSEO({
    title: "Sign In to ChessHatch - Join the Chess Learning Community",
    description: "Sign in to ChessHatch to access interactive chess tutorials, multiplayer games, puzzles, and join a supportive learning community for chess enthusiasts.",
    keywords: "chess sign in, chess login, chess account, chess platform access, chess community login",
    jsonLd: generateWebsiteJsonLd()
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Input validation
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      toast.error(emailValidation.error);
      return;
    }
    
    if (!password) {
      toast.error('Password is required');
      return;
    }
    
    // Rate limiting
    const rateLimit = checkRateLimit(`signin_${email}`, 5, 900000); // 15 minutes
    if (!rateLimit.allowed) {
      toast.error('Too many login attempts. Please try again later.');
      logSecurityEvent('rate_limit_exceeded', undefined, { email, action: 'signin' });
      return;
    }

    setLoading(true);
    const { error } = await signIn(email, password);
    
    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        toast.error('Invalid email or password');
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success('Signed in successfully!');
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Input validation
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      toast.error(emailValidation.error);
      return;
    }
    
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      toast.error(passwordValidation.error);
      return;
    }

    // Validate names
    if (!firstName.trim()) {
      toast.error('First name is required');
      return;
    }

    if (!lastName.trim()) {
      toast.error('Last name is required');
      return;
    }
    
    // Rate limiting
    const rateLimit = checkRateLimit(`signup_${email}`, 3, 3600000); // 1 hour
    if (!rateLimit.allowed) {
      toast.error('Too many signup attempts. Please try again later.');
      logSecurityEvent('rate_limit_exceeded', undefined, { email, action: 'signup' });
      return;
    }

    setLoading(true);
    const { error } = await signUp(email, password, {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      display_name: `${firstName.trim()} ${lastName.trim()}`
    });
    
    if (error) {
      if (error.message.includes('User already registered')) {
        toast.error('An account with this email already exists');
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success('Account created! Check your email to confirm your account.');
      // Bootstrap first admin if no admins exist (only on first user registration)
      try {
        const result = await supabase.rpc('bootstrap_first_admin');
        if (result.data) {
          console.log('First admin bootstrapped successfully');
        }
      } catch (error) {
        console.error('Admin bootstrap error:', error);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 dark:from-purple-900 dark:via-slate-900 dark:to-pink-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Floating orbs - only in dark mode */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-br from-teen-cyan/30 to-teen-blue/20 rounded-full blur-3xl animate-float hidden dark:block" />
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-teen-pink/25 to-teen-purple/20 rounded-full blur-3xl animate-float hidden dark:block" style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-gradient-to-br from-teen-gold/20 to-teen-orange/15 rounded-full blur-2xl animate-float hidden dark:block" style={{ animationDelay: '1s' }} />
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent mb-2">ChessHatch</h1>
          <p className="text-foreground/80 dark:text-slate-300">Sign in to play with friends</p>
        </div>

        <Card className="bg-card dark:bg-white/10 backdrop-blur-xl border-border dark:border-white/20 shadow-lg dark:shadow-[0_0_50px_rgba(139,92,246,0.2)]">
          <CardHeader>
            <CardTitle className="text-foreground dark:text-white">Welcome</CardTitle>
            <CardDescription className="text-muted-foreground dark:text-slate-300">
              Sign in to your account or create a new one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-muted dark:bg-white/10">
                <TabsTrigger value="signin" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-teen-purple data-[state=active]:to-teen-pink data-[state=active]:text-white">Sign In</TabsTrigger>
                <TabsTrigger value="signup" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-teen-cyan data-[state=active]:to-teen-blue data-[state=active]:text-white">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="text-foreground dark:text-slate-200">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-background dark:bg-white/10 border-border dark:border-white/20 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-slate-400 focus:border-primary dark:focus:border-teen-cyan"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password" className="text-foreground dark:text-slate-200">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-background dark:bg-white/10 border-border dark:border-white/20 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-slate-400 focus:border-primary dark:focus:border-teen-cyan"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-primary-foreground dark:bg-gradient-to-r dark:from-teen-purple dark:to-teen-pink dark:hover:from-teen-purple/90 dark:hover:to-teen-pink/90" disabled={loading}>
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                  <div className="text-center mt-2">
                    <Button 
                      type="button"
                      variant="link" 
                      onClick={() => navigate('/forgot-password')}
                      className="text-sm text-primary hover:text-primary/80 dark:text-teen-cyan dark:hover:text-teen-cyan/80 p-0 h-auto"
                    >
                      Forgot password?
                    </Button>
                  </div>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first-name" className="text-foreground dark:text-slate-200">First Name</Label>
                      <Input
                        id="first-name"
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        className="bg-background dark:bg-white/10 border-border dark:border-white/20 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-slate-400 focus:border-primary dark:focus:border-teen-cyan"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last-name" className="text-foreground dark:text-slate-200">Last Name</Label>
                      <Input
                        id="last-name"
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        className="bg-background dark:bg-white/10 border-border dark:border-white/20 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-slate-400 focus:border-primary dark:focus:border-teen-cyan"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-foreground dark:text-slate-200">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-background dark:bg-white/10 border-border dark:border-white/20 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-slate-400 focus:border-primary dark:focus:border-teen-cyan"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-foreground dark:text-slate-200">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="Create a password (min. 6 chars with uppercase and lowercase letters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-background dark:bg-white/10 border-border dark:border-white/20 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-slate-400 focus:border-primary dark:focus:border-teen-cyan"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-primary-foreground dark:bg-gradient-to-r dark:from-teen-cyan dark:to-teen-blue dark:hover:from-teen-cyan/90 dark:hover:to-teen-blue/90" disabled={loading}>
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground hover:bg-muted dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/10"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Auth;